import { FC, useCallback, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import Button from 'src/components/Button';
import Image from 'src/components/Image';
import ModalLayoutBase, { FooterDim, HeaderDim, MobileModalType, ModalContent as ModalContentBase, ModalFooter as ModalFooterBase } from 'src/components/ModalLayout';
import useIsRtl from 'src/hooks/useIsRtl';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { isChromeBrowser } from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';
import { GUIDE_IMAGES, STTDeniedGuideProps } from './speechRecognitionDeniedGuideConstants';
const ModalLayout = styled(ModalLayoutBase)`
  border-radius: 0;

  background: #3effd7;
  ${HeaderDim} {
    background: linear-gradient(180deg, #3effd7 0%, rgba(62, 255, 215, 0) 100%);
  }
  ${FooterDim} {
    background: linear-gradient(180deg, rgba(62, 255, 215, 0) 0%, #3effd7 100%);
  }
`;
const ModalContent = styled(ModalContentBase)`
  padding: 16px 24px;
`;
const HeaderTitle = styled.p`
  font-size: 30px;
  font-weight: 700;
  line-height: 40px;
  color: ${({
  theme
}) => theme.color.contentDeepFixed};
  margin-bottom: 16px;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  word-break: break-word;
`;
const DescriptionText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.contentDeepFixed};
  width: 100%;
  margin-bottom: 32px;

  a {
    text-decoration: underline;
    font-weight: 700;
    color: ${({
  theme
}) => theme.color.contentDeepFixed};
  }
`;
const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding-bottom: 32px;
`;
const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;
const StepLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  color: ${({
  theme
}) => theme.color.content12Fixed};
  word-break: break-all;
`;
const GuideCardImage = styled(Image)`
  width: 100%;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.13);
`;
const Footer = styled(ModalFooterBase)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px 24px;
  padding-block-start: 16px;
  width: 100%;
  flex-shrink: 0;
  background: #3effd7;
`;
const FooterText = styled.p`
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  color: ${({
  theme
}) => theme.color.content12Fixed};
  text-align: center;
`;

/**
 * iOS 브라우저에서 음성인식 권한이 거부되었을때, OS의 권한 허용 방법을 가이드하는 모달
 */
const STTDeniedGuideForiOS: FC<STTDeniedGuideProps> = ({
  referrer,
  onComplete
}) => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const isRtl = useIsRtl();
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.ANYWHERE__IMPRESSION_STT_SETTINGS_POPUP,
      eventParams: {
        action_category: 'impression',
        tab: 'anywhere',
        page: 'anywhere',
        target: 'stt_settings_popup',
        referrer
      }
    });
  }, [referrer]);
  const closeAndComplete = useCallback(() => {
    closeModal(ModalType.SPEECH_RECOGNITION_DENIED_GUIDE);
    onComplete?.();
  }, [closeModal, onComplete]);
  const handleDone = useCallback(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.STT_SETTINGS_POPUP__CLICK_DONE,
      eventParams: {
        action_category: 'click',
        tab: 'anywhere',
        page: 'stt_settings_popup',
        target: 'done',
        referrer
      }
    });
    closeAndComplete();
  }, [closeAndComplete, referrer]);
  const isChrome = useMemo(() => isChromeBrowser(), []);
  const dir = isRtl ? 'rtl' : 'ltr';
  const browser = isChrome ? 'chrome' : 'safari';
  return <ModalLayout isDim={false} mobileType={MobileModalType.SCREEN_MODAL} closeWhenClickOutside={false} closeWithESC={false} navButtonType={ModalNavButtonType.NONE} mobileNavButtonType={ModalNavButtonType.NONE} data-testid='SpeechRecognitionDeniedGuide' data-sentry-element="ModalLayout" data-sentry-component="STTDeniedGuideForiOS" data-sentry-source-file="STTDeniedGuideForiOS.tsx">
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="STTDeniedGuideForiOS.tsx">
        <HeaderTitle data-sentry-element="HeaderTitle" data-sentry-source-file="STTDeniedGuideForiOS.tsx">{t('azar_assistant.consent_nudge_title')}</HeaderTitle>
        <DescriptionText dangerouslySetInnerHTML={{
        __html: t('azar_assistant.consent_nudge_desc')
      }} data-sentry-element="DescriptionText" data-sentry-source-file="STTDeniedGuideForiOS.tsx" />
        <StepsContainer data-sentry-element="StepsContainer" data-sentry-source-file="STTDeniedGuideForiOS.tsx">
          <StepItem data-sentry-element="StepItem" data-sentry-source-file="STTDeniedGuideForiOS.tsx">
            <StepLabel data-sentry-element="StepLabel" data-sentry-source-file="STTDeniedGuideForiOS.tsx">1. {t('azar_assistant.consent_nudge_howtowork_1')}</StepLabel>
            <GuideCardImage aria-hidden src={GUIDE_IMAGES.settings[dir]} data-sentry-element="GuideCardImage" data-sentry-source-file="STTDeniedGuideForiOS.tsx" />
          </StepItem>

          <StepItem data-sentry-element="StepItem" data-sentry-source-file="STTDeniedGuideForiOS.tsx">
            <StepLabel data-sentry-element="StepLabel" data-sentry-source-file="STTDeniedGuideForiOS.tsx">2. {t('azar_assistant.consent_nudge_howtowork_2')}</StepLabel>
            <GuideCardImage aria-hidden src={GUIDE_IMAGES.privacySecurity[dir]} data-sentry-element="GuideCardImage" data-sentry-source-file="STTDeniedGuideForiOS.tsx" />
          </StepItem>

          <StepItem data-sentry-element="StepItem" data-sentry-source-file="STTDeniedGuideForiOS.tsx">
            <StepLabel data-sentry-element="StepLabel" data-sentry-source-file="STTDeniedGuideForiOS.tsx">3. {t('azar_assistant.consent_nudge_howtowork_3')}</StepLabel>
            <GuideCardImage aria-hidden src={GUIDE_IMAGES.speechRecognition[browser][dir]} data-sentry-element="GuideCardImage" data-sentry-source-file="STTDeniedGuideForiOS.tsx" />
          </StepItem>
        </StepsContainer>
      </ModalContent>
      <Footer data-sentry-element="Footer" data-sentry-source-file="STTDeniedGuideForiOS.tsx">
        <FooterText data-sentry-element="FooterText" data-sentry-source-file="STTDeniedGuideForiOS.tsx">{t('azar_assistant.consent_nudge_button_desc')}</FooterText>
        <Button id='speech-recognition-denied-done' onClick={handleDone} data-testid='SpeechRecognitionDeniedGuide-button-done' color='tertiary' data-sentry-element="Button" data-sentry-source-file="STTDeniedGuideForiOS.tsx">
          {t('azar_assistant.consent_popup_button_agree')}
        </Button>
      </Footer>
    </ModalLayout>;
};
export default STTDeniedGuideForiOS;